import { RiCloseLine } from '@remixicon/react';
import { LayerId, LayerType, SerializedLayers } from '@lidojs/design-core';
import { useEditor } from '@lidojs/design-editor';
import axios from 'axios';
import React, { FC, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useAsync } from 'react-use';

interface Frame {
  id: string;
  img: string;
  clipPath: string;
  width: number;
  height: number;
}

const FrameContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const [frames, setFrames] = useState<Frame[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { actions, query, dragNDrop } = useEditor((state) => ({
    dragNDrop: state.dragNDrop,
  }));
  useAsync(async () => {
    const response = await axios.get<string[]>(
      '/frames?page=1&limit=100&q=template'
    );
    const { data } = response;
    const nextFrames = data.map((raw) => JSON.parse(raw));
    setFrames(nextFrames);
    setIsLoading(false);
  }, []);
  const addFrame = async (data: Frame) => {
    actions.addFrameLayer(data, data.clipPath);
    if (isMobile) {
      onClose();
    }
  };

  const handleDrag = (event: React.DragEvent, frame: Frame) => {
    const { clientX, clientY } = event;
    const pageSize = query.getPageSize();
    const ratio = pageSize.width / pageSize.height;
    const frameRatio = frame.width / frame.height;
    const scale =
      ratio > frameRatio
        ? (pageSize.height * 0.5) / frame.height
        : (pageSize.width * 0.5) / frame.width;
    const data: {
      layer: LayerType;
      data: { rootId: LayerId; layers: SerializedLayers };
    } = {
      layer: 'Frame',
      data: {
        rootId: frame.id,
        layers: {
          [frame.id]: {
            type: {
              resolvedName: 'FrameLayer',
            },
            props: {
              clipPath: frame.clipPath,
              position: {
                x: 0,
                y: 0,
              },
              boxSize: {
                width: frame.width * scale,
                height: frame.height * scale,
              },
              rotate: 0,
              scale,
            },
            locked: false,
            parent: 'ROOT',
            child: [],
          },
        },
      },
    };
    actions.startDragNDrop(data, { x: clientX, y: clientY });
    event.dataTransfer.clearData('text/plain');
    event.dataTransfer.setData('text/plain', JSON.stringify(data));
    event.dataTransfer.setDragImage(new Image(), 0, 0);
  };

  return (
    <div
      css={(theme) => ({
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        display: 'flex',
      })}
    >
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 50,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          Frames
        </p>
        <div
          css={(theme) => ({
            fontSize: 20,
            flexShrink: 0,
            width: 32,
            height: 32,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
          onClick={onClose}
        >
          <RiCloseLine />
        </div>
      </div>
      <div
        css={(theme) => ({
          flexDirection: 'column',
          overflowY: 'auto',
          display: 'flex',
        })}
      >
        <div
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
            gridGap: 8,
            padding: '16px',
          })}
        >
          {isLoading && <div>Loading...</div>}
          {frames.map((item, index) => (
            <div
              key={index}
              css={(theme) => ({
                cursor: 'pointer',
                position: 'relative',
                '-webkit-user-drag': 'element',
              })}
              onClick={() => addFrame(item)}
              onDragStart={(e) => handleDrag(e, item)}
            >
              <div
                css={(theme) => ({
                  paddingBottom: '100%',
                })}
              />
              <div
                css={(theme) => ({
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
              >
                <img
                  css={(theme) => ({ maxHeight: '100%', maxWidth: '100%' })}
                  src={item.img}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FrameContent;
